<script setup lang="ts">
import { PredefinedRangeMode, RangeMode } from "@/utils/time-range";
import { mdiClockTimeEight } from "@mdi/js";
import { Button, Icon, Popover, Tooltip } from "@soft-db/ui";
import { DateTime } from "luxon";
import { TimeRange } from "../../stores";
import AppTimeRangeDialog from "../common/AppTimeRangeDialog";
import DesktopOnly from "./DesktopOnly.vue";

type RangeSelection = Record<PredefinedRangeMode, boolean>;

const {
	availableModes = {
		last_8h: true,
		last_24h: true,
		last_48h: true,
		last_7d: true,
	},
} = defineProps<{
	availableModes?: Partial<RangeSelection>;
	disableCustom?: boolean;
}>();

defineOptions({ inheritAttrs: false });

const rangeModel = defineModel<TimeRange>("range", { required: true });
const modeModel = defineModel<RangeMode>("mode", { required: true });

const { t } = useI18n();

const modeLabels = computed<Record<RangeMode, string>>(() => ({
	last_8h: t("dashboard.last8hours"),
	last_24h: t("dashboard.last24hours"),
	last_48h: t("dashboard.last48hours"),
	last_7d: t("dashboard.last7days"),
	custom: t("dashboard.customRange"),
}));

const menuModes = computed<PredefinedRangeMode[]>(() => {
	const modes: PredefinedRangeMode[] = [];
	for (const [mode, active] of Object.entries(availableModes)) {
		if (active) {
			modes.push(mode as PredefinedRangeMode);
		}
	}

	return modes;
});

const formattedStartTime = computed(() =>
	rangeModel.value.start.toLocaleString(DateTime.DATETIME_MED)
);
const formattedEndTime = computed(() => rangeModel.value.end.toLocaleString(DateTime.DATETIME_MED));

function updateRangeMode(mode: PredefinedRangeMode) {
	modeModel.value = mode;
}

const openDialog = ref(false);

function setToCustomRange() {
	modeModel.value = "custom";
}

function openCustomRangeDialog() {
	openDialog.value = true;
}
</script>

<template>
	<Popover closeOnClick side="bottom">
		<template #trigger>
			<Tooltip>
				<Button class="flex-nowrap gap-1 px-2" variant="outline" v-bind="$attrs">
					<Icon :d="mdiClockTimeEight" />
					<DesktopOnly v-if="modeModel === 'custom'">
						<div>{{ formattedStartTime }}</div>
						<div>{{ t("common.to") }}</div>
						<div>{{ formattedEndTime }}</div>
					</DesktopOnly>
					<span v-else class="hidden md:block">{{ modeLabels[modeModel] }}</span>
				</Button>

				<template #tooltip>
					<div class="text-center">
						<div>{{ formattedStartTime }}</div>
						<div>{{ t("common.to") }}</div>
						<div>{{ formattedEndTime }}</div>
					</div>
				</template>
			</Tooltip>
		</template>
		<div class="m-2 flex flex-col gap-2">
			<Button
				v-for="mode of menuModes"
				:key="mode"
				variant="ghost"
				:color="mode === modeModel ? 'success' : 'default'"
				@click="() => updateRangeMode(mode)"
			>
				{{ modeLabels[mode] }}
			</Button>
			<Button v-if="!disableCustom" color="secondary" @click="openCustomRangeDialog">
				{{ modeLabels["custom"] }}
			</Button>
		</div>
	</Popover>
	<AppTimeRangeDialog
		v-model="rangeModel"
		:dialog="openDialog"
		@close="() => (openDialog = false)"
		@update:modelValue="setToCustomRange"
	/>
</template>
